import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BlockIcon from '@mui/icons-material/Block';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { normalizeKeyword } from '../../../utils/publicTenderRelevance';
import { BaseModal } from '../../common/UI/BaseModal';

interface RelevanceProfileDialogProps {
  open: boolean;
  onClose: () => void;
  highlightedKeyword?: string | null;
  companySeeds?: string[];
  positiveKeywords: Array<{ keyword: string; count: number; isCustom?: boolean }>;
  ignoredKeywords?: string[];
  excludedKeywords: string[];
  negativeArchiveKeywords: Array<{ keyword: string; count: number }>;
  onAddPositiveKeyword: (keyword: string) => Promise<void> | void;
  onRemovePositiveKeyword: (keyword: string) => Promise<void> | void;
  onIgnoreKeyword?: (keyword: string) => Promise<void> | void;
  onRestoreIgnoredKeyword?: (keyword: string) => Promise<void> | void;
  onClearIgnoredKeywords?: () => Promise<void> | void;
  onMuteKeyword: (keyword: string) => Promise<void> | void;
  onUnmuteKeyword: (keyword: string) => Promise<void> | void;
}

const pulseKeyframes = {
  '@keyframes keywordFlashPulse': {
    '0%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(234, 88, 12, 0.85)',
    },
    '50%': {
      transform: 'scale(1.08)',
      boxShadow: '0 0 0 8px rgba(234, 88, 12, 0)',
    },
    '100%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(234, 88, 12, 0)',
    },
  },
};

interface KeywordPillProps {
  label: string;
  bg: string;
  color: string;
  border: string;
  isDarkSurface?: boolean | undefined;
  activeFlash?: boolean | undefined;
  pillRef?: React.RefObject<HTMLDivElement | null> | undefined;
  onPromote?: (() => void) | undefined;
  promoteLabel?: string | undefined;
  onDemote?: (() => void) | undefined;
  demoteLabel?: string | undefined;
  demoteTone?: 'neutral' | 'block' | undefined;
}

const KeywordPill: React.FC<KeywordPillProps> = ({
  label,
  bg,
  color,
  border,
  isDarkSurface = false,
  activeFlash = false,
  pillRef,
  onPromote,
  promoteLabel,
  onDemote,
  demoteLabel,
  demoteTone = 'neutral',
}) => {
  const promoteCircleSx = isDarkSurface
    ? {
        backgroundColor: 'rgba(74, 222, 128, 0.18)',
        color: '#86EFAC',
        border: '1px solid rgba(74, 222, 128, 0.38)',
        '&:hover': {
          backgroundColor: 'rgba(74, 222, 128, 0.3)',
        },
      }
    : {
        backgroundColor: '#DCFCE7',
        color: '#166534',
        border: '1px solid #86EFAC',
        '&:hover': {
          backgroundColor: '#BBF7D0',
        },
      };

  const demoteCircleSx = isDarkSurface
    ? {
        backgroundColor: 'rgba(251, 146, 60, 0.18)',
        color: '#FDBA74',
        border: '1px solid rgba(251, 146, 60, 0.38)',
        '&:hover': {
          backgroundColor: 'rgba(251, 146, 60, 0.32)',
        },
      }
    : demoteTone === 'block'
      ? {
          backgroundColor: '#FEE2E2',
          color: '#991B1B',
          border: '1px solid #FCA5A5',
          '&:hover': {
            backgroundColor: '#FECACA',
          },
        }
      : {
          backgroundColor: '#FFEDD5',
          color: '#9A3412',
          border: '1px solid #FDBA74',
          '&:hover': {
            backgroundColor: '#FED7AA',
          },
        };

  return (
    <Box
      ref={pillRef}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        pl: 1.2,
        pr: 0.6,
        py: 0.35,
        borderRadius: '18px',
        backgroundColor: bg,
        color,
        border,
        fontSize: '0.8125rem',
        fontWeight: 600,
        lineHeight: 1.3,
        animation: activeFlash ? 'keywordFlashPulse 0.9s ease-in-out 4' : undefined,
      }}
    >
      <span>{label}</span>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, ml: 0.15 }}>
        {onPromote && (
          <IconButton
            size="small"
            aria-label={promoteLabel || `Promote ${label}`}
            onClick={onPromote}
            sx={{
              width: 20,
              height: 20,
              p: 0,
              borderRadius: '50%',
              transition: 'background-color 0.15s ease',
              ...promoteCircleSx,
            }}
          >
            <ArrowUpwardIcon sx={{ fontSize: '0.8rem' }} />
          </IconButton>
        )}
        {onDemote && (
          <IconButton
            size="small"
            aria-label={demoteLabel || `Demote ${label}`}
            onClick={onDemote}
            sx={{
              width: 20,
              height: 20,
              p: 0,
              borderRadius: '50%',
              transition: 'background-color 0.15s ease',
              ...demoteCircleSx,
            }}
          >
            <ArrowDownwardIcon sx={{ fontSize: '0.8rem' }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

const RelevanceProfileDialog: React.FC<RelevanceProfileDialogProps> = ({
  open,
  onClose,
  highlightedKeyword,
  positiveKeywords,
  ignoredKeywords = [],
  excludedKeywords,
  negativeArchiveKeywords,
  onAddPositiveKeyword,
  onRemovePositiveKeyword,
  onIgnoreKeyword,
  onRestoreIgnoredKeyword,
  onClearIgnoredKeywords,
  onMuteKeyword,
  onUnmuteKeyword,
}) => {
  const theme = useTheme();
  const [newPositiveKw, setNewPositiveKw] = useState('');
  const [newExcludedKw, setNewExcludedKw] = useState('');
  const highlightedChipRef = useRef<HTMLDivElement | null>(null);

  const normalizedHighlight = highlightedKeyword ? normalizeKeyword(highlightedKeyword) : '';

  useEffect(() => {
    if (open && normalizedHighlight && highlightedChipRef.current) {
      const timer = setTimeout(() => {
        highlightedChipRef.current?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'center',
        });
      }, 120);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open, normalizedHighlight]);

  const handleAddPositive = async () => {
    const trimmed = newPositiveKw.trim().toLowerCase();
    if (!trimmed) {
      return;
    }
    setNewPositiveKw('');
    await onAddPositiveKeyword(trimmed);
  };

  const handleAddExcluded = async () => {
    const trimmed = newExcludedKw.trim().toLowerCase();
    if (!trimmed) {
      return;
    }
    setNewExcludedKw('');
    await onMuteKeyword(trimmed);
  };

  const isHighlighted = (kw: string) =>
    Boolean(normalizedHighlight && normalizeKeyword(kw) === normalizedHighlight);

  const isHighlightHandled =
    Boolean(normalizedHighlight) &&
    (excludedKeywords.some((k) => normalizeKeyword(k) === normalizedHighlight) ||
      ignoredKeywords.some((k) => normalizeKeyword(k) === normalizedHighlight));

  const handleDemoteToNeutral = (kw: string) => {
    if (onIgnoreKeyword) {
      onIgnoreKeyword(kw);
    } else {
      onRemovePositiveKeyword(kw);
    }
  };

  const handlePromoteFromBlocked = (kw: string) => {
    if (onIgnoreKeyword) {
      onIgnoreKeyword(kw);
    } else {
      onUnmuteKeyword(kw);
    }
  };

  const combinedBlockedKeywords = Array.from(
    new Set([
      ...excludedKeywords,
      ...negativeArchiveKeywords
        .filter((item) => !excludedKeywords.includes(item.keyword))
        .map((item) => item.keyword),
    ])
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      aria-labelledby="relevance-profile-dialog-title"
      maxWidth="md"
      fullWidth
      title="Tender Relevance & Match Profile"
      actions={
        <Button onClick={onClose} variant="contained" color="primary">
          Done
        </Button>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1, ...pulseKeyframes }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
          Use the <strong>&uarr;</strong> and <strong>&darr;</strong> arrows on any keyword to
          promote or demote it between <strong>Boosted</strong>, <strong>Neutral</strong>, and{' '}
          <strong>Blocked</strong>.
        </Typography>

        {normalizedHighlight && !isHighlightHandled && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: '#1E293B',
              color: '#FFFFFF',
              border: '2px solid #EA580C',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box sx={{ flex: '1 1 260px' }}>
              <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                Selected keyword: &ldquo;{normalizedHighlight}&rdquo;
              </Typography>
              <Typography variant="caption" sx={{ color: '#E2E8F0', display: 'block' }}>
                Choose <strong>Stop Boosting</strong> to demote to Neutral without hiding the
                tender, or <strong>Block</strong> to hide any tender with this term.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<ArrowDownwardIcon fontSize="small" />}
                onClick={() => handleDemoteToNeutral(normalizedHighlight)}
                sx={{
                  backgroundColor: '#F8FAFC',
                  color: '#0F172A',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#E2E8F0',
                  },
                }}
              >
                Stop Boosting &ldquo;{normalizedHighlight}&rdquo;
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<BlockIcon fontSize="small" />}
                onClick={() => onMuteKeyword(normalizedHighlight)}
                sx={{
                  backgroundColor: '#DC2626',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#B91C1C',
                  },
                }}
              >
                Block &ldquo;{normalizedHighlight}&rdquo;
              </Button>
            </Box>
          </Paper>
        )}

        {/* Section 1: Unified Active Match Keywords */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TuneIcon fontSize="small" sx={{ color: theme.palette.text.primary }} />
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{ color: theme.palette.text.primary }}
            >
              Active Match Keywords
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1.5, color: theme.palette.text.primary }}
          >
            Your core capability keywords used to recommend tenders. Add custom keywords below or
            click <strong>&darr;</strong> to demote a keyword to Neutral.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, maxWidth: 420 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Add capability keyword (e.g. human factors, saas)"
              value={newPositiveKw}
              onChange={(e) => setNewPositiveKw(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddPositive();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Add positive keyword"
                      onClick={handleAddPositive}
                      disabled={!newPositiveKw.trim()}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {positiveKeywords.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.primary }}
              fontStyle="italic"
            >
              No active match keywords yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {positiveKeywords.map(({ keyword }) => {
                const activeFlash = isHighlighted(keyword);
                return (
                  <KeywordPill
                    key={keyword}
                    label={keyword}
                    bg={activeFlash ? '#9A3412' : '#F1F5F9'}
                    color={activeFlash ? '#FFFFFF' : '#0F172A'}
                    border={activeFlash ? '2px solid #EA580C' : '1px solid #64748B'}
                    isDarkSurface={activeFlash}
                    activeFlash={activeFlash}
                    pillRef={activeFlash ? highlightedChipRef : undefined}
                    onDemote={() => handleDemoteToNeutral(keyword)}
                    demoteLabel={`Demote ${keyword} to neutral`}
                    demoteTone="neutral"
                  />
                );
              })}
            </Box>
          )}
        </Box>

        <Divider />

        {/* Section 2: Neutral / Unboosted Keywords */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RemoveCircleOutlineIcon
                fontSize="small"
                sx={{ color: theme.palette.text.primary }}
              />
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ color: theme.palette.text.primary }}
              >
                Neutral / Unboosted Keywords
              </Typography>
            </Box>
            {ignoredKeywords.length > 0 && onClearIgnoredKeywords && (
              <Button
                size="small"
                variant="text"
                onClick={onClearIgnoredKeywords}
                sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0 }}
              >
                Clear list
              </Button>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1, color: theme.palette.text.primary }}
          >
            These terms neither boost nor block tenders. Click <strong>&uarr;</strong> to promote
            back to Boosted, or <strong>&darr;</strong> to demote to Blocked.
          </Typography>
          {ignoredKeywords.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.primary }}
              fontStyle="italic"
            >
              No neutral keywords yet. Demote any boosted or blocked keyword using the arrows to
              place it here.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {ignoredKeywords.map((kw) => {
                const activeFlash = isHighlighted(kw);
                return (
                  <KeywordPill
                    key={`ignored-${kw}`}
                    label={kw}
                    bg={activeFlash ? '#9A3412' : '#E2E8F0'}
                    color={activeFlash ? '#FFFFFF' : '#0F172A'}
                    border={activeFlash ? '2px solid #EA580C' : '1px dashed #475569'}
                    isDarkSurface={activeFlash}
                    activeFlash={activeFlash}
                    pillRef={activeFlash ? highlightedChipRef : undefined}
                    onPromote={
                      onRestoreIgnoredKeyword ? () => onRestoreIgnoredKeyword(kw) : undefined
                    }
                    promoteLabel={`Promote ${kw} to active match`}
                    onDemote={() => onMuteKeyword(kw)}
                    demoteLabel={`Demote ${kw} to blocked`}
                    demoteTone="block"
                  />
                );
              })}
            </Box>
          )}
        </Box>

        <Divider />

        {/* Section 4: Blocked & Excluded Keywords */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BlockIcon fontSize="small" color="error" />
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{ color: theme.palette.text.primary }}
            >
              Blocked &amp; Excluded Keywords
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1.5, color: theme.palette.text.primary }}
          >
            Tenders matching blocked terms are actively hidden from Recommended. Click{' '}
            <strong>&uarr;</strong> to promote a blocked term up to Neutral.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, maxWidth: 420 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Block keyword (e.g. trailers, plumbing, clinical)"
              value={newExcludedKw}
              onChange={(e) => setNewExcludedKw(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddExcluded();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Add excluded keyword"
                      onClick={handleAddExcluded}
                      disabled={!newExcludedKw.trim()}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {combinedBlockedKeywords.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.primary }}
              fontStyle="italic"
            >
              No blocked keywords yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {combinedBlockedKeywords.map((kw) => (
                <KeywordPill
                  key={`blocked-${kw}`}
                  label={kw}
                  bg="#FEF2F2"
                  color="#991B1B"
                  border="1px solid #DC2626"
                  onPromote={() => handlePromoteFromBlocked(kw)}
                  promoteLabel={`Promote ${kw} to neutral`}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </BaseModal>
  );
};

export default RelevanceProfileDialog;
